// Story 10.1 — Hooks tipados sobre la API REST. Las stories de Epic 10 los
// reusan sin tener que pensar en cache keys o invalidations.
// Story 13.x cleanup — añadido `useUpdateBotStatus` para los controles
// activar/pausar/detener desde BotPage.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { Capability } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-client';

export interface BotRowSummary {
  id: number;
  name: string;
  status: 'inactive' | 'active' | 'error';
  phoneNumberId: string | null;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  /** Story 16.2 — organización dueña y lo que el usuario puede hacer sobre el bot. */
  organizationId: number;
  organization: { id: number; name: string; slug: string } | null;
  capabilities: Capability[];
}

export function useBots() {
  return useQuery({
    queryKey: queryKeys.bots(),
    queryFn: () => api.get<BotRowSummary[]>('/api/v1/bots'),
  });
}

export function useBot(id: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.bot(id),
    queryFn: () => api.get<BotRowSummary>(`/api/v1/bots/${id}`),
    enabled,
  });
}

/**
 * Story 13.x — actualiza el campo `status` del bot via PUT /bots/:id.
 * Invalida tanto la query del bot individual como la lista del dashboard.
 */
export function useUpdateBotStatus(botId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: 'active' | 'inactive' | 'error') =>
      api.put<BotRowSummary>(`/api/v1/bots/${botId}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bot(botId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.bots() });
    },
  });
}
